/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { actions, projectStatusByCode, projectStatusConfig } from 'entities';

import { StatusSelect } from '../../components';

import { useUser } from 'hooks';
import { useToast } from 'providers/Toast';
import { useJustification } from 'providers/Justification';
import { useProjectProfile } from '../context';

import { MESSAGES, completionValidations } from '../utils';
import { EntityStatusTag } from 'components/Entities';

export function ProjectStatus() {
  const { project, setTab, updateProject } = useProjectProfile();

  const user = useUser();
  const { addToast } = useToast();

  const { openJustificationModal } = useJustification();

  const { t } = useLang();

  const doneCode = projectStatusConfig.done.code;

  const changeStatus = actions.projects.changeProjectStatus.use({
    onSuccess: (_, { status }) => {
      updateProject((old) => ({
        ...old,
        status,
      }));
    },
  });

  const executeStatusChange = useCallback(
    async (status: string, justification?: string) => {
      await changeStatus.execute({
        project: project.id,
        status,
        justification,
      });
    },
    [project, changeStatus],
  );

  const statusConfig = projectStatusByCode[project.status];

  const handleStatusChange = useCallback(
    async (status: string) => {
      if (status === project.status) return;

      const isDoneChange = status === doneCode;

      const doneValidationResult =
        isDoneChange && completionValidations.find(({ checkError }) => checkError(project));

      if (doneValidationResult) return addToast(doneValidationResult.getError(t));

      if (isDoneChange) return setTab('finish');

      const toStatus = projectStatusByCode[status];

      const textGetter = toStatus.changes?.getTo || statusConfig.changes?.getFrom;

      if (!textGetter) return executeStatusChange(status);

      openJustificationModal({
        text: textGetter(t),
        cancel: () => addToast(MESSAGES.statusChangeAborted(t)),
        onConfirm: (justification) => executeStatusChange(status, justification),
      });
    },
    [
      project,
      addToast,
      setTab,
      executeStatusChange,
      openJustificationModal,
      statusConfig,
      doneCode,
      t,
    ],
  );

  const loggedUserIsOwner = project?.owner.id === user.id;

  const statusBlock = [doneCode, projectStatusConfig.notStated.code].some(
    (code) => code === project?.status,
  );

  const shouldDisplaySelect = !statusBlock && loggedUserIsOwner && statusConfig.displayOnSelect;

  return shouldDisplaySelect ? (
    <StatusSelect
      value={project.status}
      onChange={handleStatusChange}
      statusConfig={projectStatusConfig as any}
      loading={changeStatus.loading}
    />
  ) : (
    <EntityStatusTag
      target="project"
      status={project.status}
      round={false}
      className="min-h-7"
      size="medium"
    />
  );
}
